import React, {
  MouseEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import styles from "./machines-remote-support-active-button.module.scss";
import { VerticalSeparator } from "@src/components/atoms/vertical-separator/vertical-separator";
import requestRemoteUserIcon from "@src/assets/svg/request-remote-access-user-green-icon.svg";
import unionThreeDotsIcon from "@src/assets/svg/triple-dots-icon.svg";
import { VncSettingsModal } from "@src/components/organisms/modals/vnc-settings-modal/vnc-settings-modal";
import type { VncSettings } from "@src/cloud-connect/vnc/utils/novnc-loader";
import { decodeIso8601DurationToTimePeriod } from "../../../cloud-connect/utils/decode-iso-8601-duration-from-time-period";
import { convertTimePeriodToSeconds } from "../../../cloud-connect/utils/convert-time-period-to-seconds";
import UpRightGreenArrowIcon from "@src/assets/svg/up-right-green-arrow.svg";
import { useI18N } from "@src/language/language-context";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import { CustomTooltip } from "@src/components/atoms/custom-tooltip/custom-tooltip";
import {
  MachinesListSupportSessionsStatusTypes,
  MachinesListSupportSessionsZitadelRoleTypes,
} from "@common/types/machines-list-types";
import { formatDuration } from "@src/cloud-connect/vpn-manager/utils/format-duration-string";
import { isActiveOtherUserView } from "@src/cloud-connect/vpn-manager/utils/active-other-user";
import cn from "classnames";

const cssClassName = "machines-remote-support-active-button";

interface MachinesListRemoteSupportActiveButtonPropsTypes {
  onViewDetails: (e: MouseEvent | KeyboardEvent) => void;
  onCancel: (e: MouseEvent | KeyboardEvent) => void;
  requestedDuration?: string;
  buttonText: string;
  loading: boolean;
  zitadelRole: MachinesListSupportSessionsZitadelRoleTypes;
  status: MachinesListSupportSessionsStatusTypes;
  buttonVariant?: string;
  setLocalRemainingDuration?: (remainingSeconds: number) => void;
  disableAllActions?: boolean;
  tooltip?: string;
  isCurrentUserSessionOwner?: boolean;
  isPrivateSession?: boolean;
  onActiveOtherUserViewChange?: (isVisible: boolean) => void;
  showCancelIcon?: boolean;
  onEnterScreensharing?: (settings: VncSettings) => void;
  onDisconnect?: () => void;
  disconnecting?: boolean;
}

export const MachinesListRemoteSupportActiveButton = ({
  onViewDetails,
  onCancel,
  requestedDuration,
  buttonText,
  loading,
  zitadelRole,
  status,
  buttonVariant = "active-modal-button",
  setLocalRemainingDuration,
  disableAllActions = false,
  tooltip = "",
  isCurrentUserSessionOwner = false,
  isPrivateSession = false,
  onActiveOtherUserViewChange,
  showCancelIcon = true,
  onEnterScreensharing,
  onDisconnect,
  disconnecting = false,
}: MachinesListRemoteSupportActiveButtonPropsTypes) => {
  const i18n = useI18N();
  const optionsAnchorRef = useRef<HTMLButtonElement>(null);
  const [showOptionsOverlay, setShowOptionsOverlay] = useState(false);
  const timePeriod = useMemo(
    () => decodeIso8601DurationToTimePeriod(requestedDuration),
    [requestedDuration],
  );

  const endTime = useMemo(() => {
    const seconds = convertTimePeriodToSeconds(timePeriod);
    return Date.now() + seconds * 1000;
  }, [timePeriod]);

  const totalSeconds = useMemo(
    () => Math.max(0, convertTimePeriodToSeconds(timePeriod)),
    [timePeriod],
  );

  const [remainingSeconds, setRemainingSeconds] = useState<number>(() =>
    Math.max(0, Math.floor((endTime - Date.now()) / 1000)),
  );

  const handleCancelClick = (e: MouseEvent | KeyboardEvent) => {
    e.stopPropagation();
    if (disableAllActions || disconnecting) return;
    onCancel(e);
  };

  const handleViewClick = (e: MouseEvent | KeyboardEvent) => {
    e.stopPropagation();
    if (disconnecting) return;
    onViewDetails(e);
  };
  const texts = {
    activeSession: i18n(
      "modules.machinesRemoteSupportActiveButton.activeSession",
    ),
    redirect: i18n("modules.machinesRemoteSupportActiveButton.redirect"),
    activeOtherUser: i18n(
      "modules.machinesRemoteSupportActiveButton.activeOtherUser",
    ),
    awaitingTechnicianTooltip: i18n(
      "modules.machinesRemoteSupportActiveButton.awaitingTechnicianTooltip",
    ),
    activeOtherUserTooltip: i18n(
      "modules.machinesRemoteSupportActiveButton.activeOtherUserTooltip",
    ),
    privateActiveOtherUserTooltip: i18n(
      "modules.machinesRemoteSupportActiveButton.privateActiveOtherUserTooltip",
    ),
  };

  useEffect(() => {
    const updateRemainingTime = () => {
      const diff = Math.max(0, Math.floor((endTime - Date.now()) / 1000));
      setLocalRemainingDuration && setLocalRemainingDuration(diff);
      setRemainingSeconds(diff);
    };
    updateRemainingTime();
    const id = window.setInterval(updateRemainingTime, 1000);
    return () => window.clearInterval(id);
  }, [endTime]);

  let isTicketVariant = false;
  switch (buttonVariant) {
    case "request-modal-ticket-button":
      isTicketVariant = true;
      break;
    default:
      isTicketVariant = false;
      break;
  }
  const isOperatorView =
    zitadelRole === MachinesListSupportSessionsZitadelRoleTypes.ACCEPT_VPN;
  const isOwnerTechnicianView =
    zitadelRole === MachinesListSupportSessionsZitadelRoleTypes.REQUEST_VPN &&
    isCurrentUserSessionOwner;
  const isNonOwnerTechnicianView = isActiveOtherUserView({
    zitadelRole,
    isCurrentUserSessionOwner,
  });
  const isPrivateSessionRestricted = isPrivateSession && !isOwnerTechnicianView;
  const isPublicSessionNotStarted =
    (status === MachinesListSupportSessionsStatusTypes.ACCEPTED ||
      status === MachinesListSupportSessionsStatusTypes.OPEN) &&
    !isPrivateSession &&
    !isOwnerTechnicianView;

  const isDisabled = useMemo(
    () =>
      disableAllActions ||
      isPrivateSessionRestricted ||
      isPublicSessionNotStarted,
    [disableAllActions, isPrivateSessionRestricted, isPublicSessionNotStarted],
  );
  const disabledButtonStyle = isDisabled ? { opacity: 0.4 } : undefined;

  const activeButtonTextToShow = isNonOwnerTechnicianView
    ? texts.activeOtherUser
    : buttonText;

  useEffect(() => {
    onActiveOtherUserViewChange?.(isNonOwnerTechnicianView);

    return () => {
      onActiveOtherUserViewChange?.(false);
    };
  }, [isNonOwnerTechnicianView, onActiveOtherUserViewChange]);

  let resolvedTooltip = tooltip;
  if (!disableAllActions) {
    switch (true) {
      case isOperatorView && isPrivateSession:
        resolvedTooltip = texts.privateActiveOtherUserTooltip;
        break;
      case isOperatorView:
        resolvedTooltip = texts.awaitingTechnicianTooltip;
        break;
      case isOwnerTechnicianView:
        resolvedTooltip = "";
        break;
      case isNonOwnerTechnicianView && isPrivateSession:
        resolvedTooltip = texts.privateActiveOtherUserTooltip;
        break;
      case isNonOwnerTechnicianView:
        resolvedTooltip = texts.activeOtherUserTooltip;
        break;
      default:
        resolvedTooltip = tooltip;
        break;
    }
  }

  let showRedirectArrow = false;
  switch (true) {
    case isOwnerTechnicianView:
      showRedirectArrow = true;
      break;
    default:
      showRedirectArrow = false;
      break;
  }

  return (
    <div className={styles[cssClassName]}>
      <CustomTooltip
        content={resolvedTooltip}
        maxWidth="250px"
        className={styles[`${cssClassName}__tooltip-wrapper`]}
      >
        <ButtonWithLoader
          fun={handleViewClick}
          text={buttonText}
          variant={buttonVariant}
          loading={loading}
          disabled={isDisabled}
          style={{
            ...(isPrivateSessionRestricted ? { cursor: "default" } : {}),
            ...(disabledButtonStyle ?? {}),
          }}
          spinnerVariant={["success", "small"]}
        >
          <img
            className={styles[`${cssClassName}__icon`]}
            src={requestRemoteUserIcon}
            alt={texts.activeSession}
          />
          <span
            className={cn(styles[`${cssClassName}__text`], {
              [styles[`${cssClassName}__text--extra-small`]]: isTicketVariant,
            })}
          >
            <span className={styles[`${cssClassName}__active-label`]}>
              {activeButtonTextToShow}
            </span>
            <span className={styles[`${cssClassName}__dash`]}> — </span>
            <span className={styles[`${cssClassName}__time`]}>
              {formatDuration(remainingSeconds)}
            </span>
          </span>

          {showRedirectArrow && (
            <img
              className={styles[`${cssClassName}__icon`]}
              src={UpRightGreenArrowIcon}
              alt={texts.redirect}
            />
          )}
        </ButtonWithLoader>
      </CustomTooltip>

      {showRedirectArrow && onEnterScreensharing && onDisconnect && (
        <>
          <VerticalSeparator />

          <button
            ref={optionsAnchorRef}
            type="button"
            className={styles[`${cssClassName}__options-button`]}
            onClick={(e) => {
              e.stopPropagation();
              if (disconnecting) return;
              setShowOptionsOverlay((prev) => !prev);
            }}
            aria-label="Session options"
            aria-haspopup="dialog"
            aria-expanded={showOptionsOverlay}
          >
            <img
              className={styles[`${cssClassName}__options-icon`]}
              src={unionThreeDotsIcon}
              alt=""
            />
          </button>
        </>
      )}

      {showCancelIcon && (
        <>
          <VerticalSeparator />

          <CustomTooltip content={disableAllActions ? resolvedTooltip : ""}>
            <button
              type="button"
              className={`${styles[`${cssClassName}__cancel-button`]}`}
              onClick={handleCancelClick}
              aria-label="Cancel active session"
              disabled={disableAllActions}
            >
              <span className={styles[`${cssClassName}__cancel-icon`]}>×</span>
            </button>
          </CustomTooltip>
        </>
      )}

      {showRedirectArrow &&
        showOptionsOverlay &&
        onEnterScreensharing &&
        onDisconnect && (
        <VncSettingsModal
          anchorRef={optionsAnchorRef}
          remainingSeconds={remainingSeconds}
          totalSeconds={totalSeconds}
          disconnecting={disconnecting}
          onEnterScreensharing={(settings) => {
            setShowOptionsOverlay(false);
            onEnterScreensharing(settings);
          }}
          onDisconnect={onDisconnect}
          onClose={() => setShowOptionsOverlay(false)}
        />
      )}
    </div>
  );
};
